import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Typography } from '@mui/material'
import jsQR from 'jsqr'
import axios from 'axios'

import {
  boothCheckin,
  type BoothResponse,
  getBoothStatus,
  type RemoteCall,
} from '@/features/remoteGuidance'
import { BoothVideo } from '@/features/videoCall'
import { useMyPharmacyQuery } from '@/features/yakureki'

// 遠隔服薬指導のブース画面 (back log/250)。頼む店の据え置き端末で、店のアカウントでログインしたまま開いておく。
// 待ち受け → 番号札の QR をかざす (着席) → 遠隔の店が「指導を始める」を押していれば通話、
// まだなら「まもなくおつなぎします」で待つ → 指導を終えたら案内して待ち受けに戻る。
// 患者は何も押さない。

const BACK_TO_SCAN_MS = 10_000
const POLL_MS = 2_000

type View =
  | { kind: 'scan' }
  | { kind: 'message'; text: string; sub?: string }
  | { kind: 'waiting'; remoteRequestId: string; queueNumber: string | null }
  | {
      kind: 'call'
      remoteRequestId: string
      queueNumber: string | null
      call: RemoteCall
    }

const ENDED_MESSAGE: Record<string, { text: string; sub?: string }> = {
  finished: { text: 'ご説明は以上です', sub: 'お薬は窓口でお渡しします' },
  cancelled: { text: '窓口にお声がけください' },
  declined: { text: '窓口にお声がけください' },
}

const BoothPage = () => {
  const { data: pharmacy, isLoading } = useMyPharmacyQuery()
  const [view, setView] = useState<View>({ kind: 'scan' })

  const apply = useCallback((res: BoothResponse, atCheckin: boolean) => {
    const ended = ENDED_MESSAGE[res.state]
    if (ended) {
      setView({
        kind: 'message',
        ...(atCheckin && res.state === 'finished'
          ? { text: '指導は終わっています', sub: 'お薬は窓口でお渡しします' }
          : ended),
      })
      return
    }
    if (res.call) {
      setView({
        kind: 'call',
        remoteRequestId: res.id,
        queueNumber: res.queue_number,
        call: res.call,
      })
      return
    }
    setView({
      kind: 'waiting',
      remoteRequestId: res.id,
      queueNumber: res.queue_number,
    })
  }, [])

  const handleQr = useCallback(
    async (qr: string) => {
      try {
        apply(await boothCheckin(qr), true)
      } catch (err) {
        // 404 = この番号の依頼が無い・400 = この店・今日の札ではない。それ以外 (通信の途切れ等) は
        // 札のせいにせず、かざし直してもらう
        const code = axios.isAxiosError(err) ? err.response?.status : undefined
        if (code === 404 || code === 400) {
          setView({
            kind: 'message',
            text: '窓口にお声がけください',
            sub: code === 400 ? 'この番号札は使えません' : undefined,
          })
          return
        }
        setView({ kind: 'message', text: 'もう一度かざしてください' })
      }
    },
    [apply]
  )

  // 着席の後は状態を見続ける (通話が開いたら入る・終わったら案内)
  const remoteRequestId =
    view.kind === 'waiting' || view.kind === 'call'
      ? view.remoteRequestId
      : null
  const inCall = view.kind === 'call'
  useEffect(() => {
    if (!remoteRequestId) return
    const timer = setInterval(async () => {
      try {
        const res = await getBoothStatus(remoteRequestId)
        // 通話中は、終わるまで画面を変えない (同じ通話に入り直さない)
        if (inCall && res.call && !ENDED_MESSAGE[res.state]) return
        apply(res, false)
      } catch {
        // 一時的な失敗は次の tick で取り直す
      }
    }, POLL_MS)
    return () => clearInterval(timer)
  }, [remoteRequestId, inCall, apply])

  // 案内を出したら少しして待ち受けに戻る
  useEffect(() => {
    if (view.kind !== 'message') return
    const timer = setTimeout(() => setView({ kind: 'scan' }), BACK_TO_SCAN_MS)
    return () => clearTimeout(timer)
  }, [view])

  if (isLoading) return null
  if (!pharmacy?.remote_guidance?.to_pharmacy_name) {
    return <Screen text="この店舗では使用できません" />
  }

  switch (view.kind) {
    case 'scan':
      return <Scanner onQr={handleQr} />
    case 'message':
      return <Screen text={view.text} sub={view.sub} />
    case 'waiting':
      return (
        <Screen
          text={`${view.queueNumber ?? ''} 番の方`}
          sub="まもなく薬剤師におつなぎします"
        />
      )
    case 'call':
      return (
        <BoothVideo
          livekitUrl={view.call.livekit_url}
          livekitToken={view.call.livekit_token}
          onDisconnected={() =>
            setView({
              kind: 'waiting',
              remoteRequestId: view.remoteRequestId,
              queueNumber: view.queueNumber,
            })
          }
        />
      )
  }
}

const Screen = ({ text, sub }: { text: string; sub?: string }) => (
  <Box
    sx={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2,
      bgcolor: 'background.default',
    }}
  >
    <Typography sx={{ fontSize: 40, fontWeight: 600 }}>{text}</Typography>
    {sub && <Typography sx={{ fontSize: 28 }}>{sub}</Typography>}
  </Box>
)

/** カメラで番号札の QR を読む。USB の QR リーダー (キーボード入力) でも読める */
const Scanner = ({ onQr }: { onQr: (qr: string) => void }) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [typed, setTyped] = useState('')
  const [cameraError, setCameraError] = useState(false)

  useEffect(() => {
    let stream: MediaStream | null = null
    let timer: ReturnType<typeof setInterval> | undefined
    let done = false
    const canvas = document.createElement('canvas')
    navigator.mediaDevices
      ?.getUserMedia({ video: { facingMode: 'user' } })
      .then((s) => {
        stream = s
        const video = videoRef.current
        if (!video) return
        video.srcObject = s
        void video.play()
        timer = setInterval(() => {
          if (done || video.videoWidth === 0) return
          canvas.width = video.videoWidth
          canvas.height = video.videoHeight
          const ctx = canvas.getContext('2d')
          if (!ctx) return
          ctx.drawImage(video, 0, 0)
          const img = ctx.getImageData(0, 0, canvas.width, canvas.height)
          const code = jsQR(img.data, img.width, img.height)
          if (code?.data) {
            done = true
            onQr(code.data)
          }
        }, 300)
      })
      .catch(() => setCameraError(true))
    return () => {
      done = true
      if (timer) clearInterval(timer)
      stream?.getTracks().forEach((t) => t.stop())
    }
  }, [onQr])

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        bgcolor: 'background.default',
      }}
    >
      <Typography sx={{ fontSize: 36, fontWeight: 600 }}>
        番号札の QR をカメラにかざしてください
      </Typography>
      {cameraError ? (
        <Typography color="text.secondary">
          カメラが使えません。QR リーダーで読み取ってください
        </Typography>
      ) : (
        <Box
          component="video"
          ref={videoRef}
          muted
          playsInline
          sx={{
            width: 480,
            maxWidth: '90vw',
            borderRadius: 2,
            transform: 'scaleX(-1)',
          }}
        />
      )}
      {/* USB の QR リーダーは読んだ文字を打ち込んで Enter を送る */}
      <Box
        component="input"
        autoFocus
        value={typed}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setTyped(e.target.value)
        }
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Enter' && typed) {
            onQr(typed)
            setTyped('')
          }
        }}
        aria-label="QR リーダーの入力"
        sx={{ opacity: 0, position: 'absolute', pointerEvents: 'none' }}
      />
    </Box>
  )
}

export default BoothPage
